import { ChangeDetectionStrategy, Component, DestroyRef, ViewEncapsulation, effect, inject, model, output, signal, untracked } from '@angular/core';
import { RouterLink } from '@angular/router';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { TableModule } from 'primeng/table';
import { DecimalPlacesCustomPipe } from '../../../../@core/pipes';
import { BaseListCombinedService } from '../../../../@core/services/base-list-combined.service';
import { SettingsService } from '../../../../@core/services/settings.service';
import { Document, DocumentLineItem, DocumentLineItemType, DocumentProject, DocumentStatusType } from '../../../../generated/generated-types';
import { ActionBarItemsComponent } from '../../../../shared/components/action-bar-items/action-bar-items.component';
import { LocaleDatePipe } from '../../../../shared/pipes/locale-date.pipe';
import { DocumentLineItemService } from '../../services/document-line-item.service';
import { WidgetGenericDocumentListComponent } from '../widget-generic-document-list/widget-generic-document-list.component';

/** Row shape once the DocumentLineItem GraphQL fragment includes referenced advance invoice document fields. */
type AdvancePaymentListRow = DocumentLineItem & {
  referenceDocument?: { id?: string | null; date?: string | null } | null;
};

@Component({
  selector: 'app-document-advance-payments-list-widget',
  imports: [TranslatePipe, DecimalPlacesCustomPipe, TableModule, ButtonModule, MessageModule, ActionBarItemsComponent, RouterLink, LocaleDatePipe],
  templateUrl: './document-advance-payments-list-widget.component.html',
  providers: [DocumentLineItemService],
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.Default
})
export class DocumentAdvancePaymentsListWidgetComponent {
  items = signal<AdvancePaymentListRow[]>([]);
  selectedItems = signal<AdvancePaymentListRow[]>([]);
  decimalPlaces = signal<number>(2);
  document = model<Document | null>(null);
  documentId = model<string | null>(null);
  loading = signal(false);
  editItem = signal<DocumentLineItem | null>(null);
  editIndex = signal<number | null>(null);
  documentProject = model<DocumentProject | null>(null);
  documentChanged = output<void>();
  DocumentStatusType = DocumentStatusType;
  destroyRef = inject(DestroyRef);

  constructor(
    private documentLineItemService: DocumentLineItemService,
    private settingsService: SettingsService,
    private baseListServices: BaseListCombinedService
  ) {
    this.decimalPlaces.set(this.settingsService.getSetting("S_TENANT_DOCUMENT_LINE_ITEM_ROUND_DECIMAL_PLACES"));

    effect(() => {
      let documentId = this.documentId();
      untracked(() => {
        if (documentId) {
          this.reset();
          this.loadItems();
        }
        else {
          this.reset();
        }
      })
    })

    this.destroyRef.onDestroy(() => {
      this.reset();
      this.document = null;
      this.documentId = null;
      this.documentProject = null;
      console.log(`Destroying ${this.constructor.name} component`);
    });
  }

  reset() {
    this.selectedItems.set([]);
    this.editItem.set(null);
    this.editIndex.set(null);
    this.loading.set(false);
    this.items.set([]);
  }

  loadItems() {
    this.loading.set(true);
    this.documentLineItemService.getItemsOfDocument(this.document()?.id, [DocumentLineItemType.ADVANCE]).then((items) => {
      this.items.set(items as AdvancePaymentListRow[]);
      this.loading.set(false);
    }).catch((error) => {
      console.error('Error loading advance payment items:', error);
      this.loading.set(false);
    });
  }

  addNew() {
    const excludeDocumentIdSet = new Set<string>();
    excludeDocumentIdSet.add(this.documentId());
    if (this.document()?.invoiceDocumentReferences?.length > 0)
      for (const item of this.document().invoiceDocumentReferences) {
        excludeDocumentIdSet.add(item.documentId);
      }
    const excludeDocumentIds = Array.from(excludeDocumentIdSet);

    // open modal window with document list widget
    this.baseListServices.modalService.fromComponent(WidgetGenericDocumentListComponent, {
      title: this.baseListServices.i18nService.translate(_("document.advance-payments-invoices-from-project")) + " " + this.documentProject()?.identifier,
      closable: true,
      maximizable: true,
      size: 'lg',
      locals: {
        checkboxMode: true,
        noAddNew: true,
        noCopy: true,
        noBaseEdit: true,
        noDelete: true,
        currentSearchInput: {
          financeRelevant: true,
          documentProjectId: this.documentProject()?.id,
          excludeDocumentIds: excludeDocumentIds
        }
      },
      setLocalsSig: {
        noDoubleClick: true,
        viewName: 'AdvancePaymentsDocumentList',
      }
    }).subscribe((result: Document[]) => {
      if (result) {
        let documentIds = result.map(x => x.id);
        this.documentLineItemService.saveAdvancePaymentInvoicesAsDLIs(documentIds, this.document()?.id).then((items) => {
          this.items.set([...this.items(), ...(items as AdvancePaymentListRow[])]);
          this.baseListServices.notificationService.success(this.baseListServices.i18nService.translate(_('common.saved')));
          this.documentChanged.emit();
        });
      }
    });
  }

  deleteItem(items: AdvancePaymentListRow[]) {
    this.loading.set(true);
    let itemIds = items.map(x => x.id);
    this.documentLineItemService.deleteAdvancePaymentInvoices(itemIds, this.document()?.id).then((items) => {
      this.items.set(this.items().filter(x => !itemIds.includes(x.id)));
      this.baseListServices.notificationService.success(this.baseListServices.i18nService.translate(_('common.deleted')));
      this.documentChanged.emit();
    }).catch((error) => {
      console.error('Error deleting advance payment items:', error);
      this.loading.set(false);
    }).finally(() => {
      this.loading.set(false);
    });
  }

  refreshItems() {
    this.loading.set(true);
    this.documentLineItemService.getItemsOfDocument(this.document()?.id, [DocumentLineItemType.ADVANCE]).then((items) => {
      this.items.set(items as AdvancePaymentListRow[]);
      this.loading.set(false);
    }).catch((error) => {
      console.error('Error refreshing advance payment items:', error);
      this.loading.set(false);
    });
  }

  /** Same route pattern as document relation links (`/documents/edit/:id`). */
  getReferencedDocumentUrl(documentId: string): string {
    return `/documents/edit/${documentId}`;
  }

  onEditInit(item: AdvancePaymentListRow, index: number) {
    // this.editItem.set(item);
    // this.editIndex.set(index);
    // TODO: Implement later if needed
  }
}
